<template>
  <div class="flex flex-wrap">
    <div class="w-full mt-5">
      <div class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-white border-0">
        
        <!-- Header -->
        <div class="rounded-t bg-white mb-0 px-6 py-4 border-b">
          <div class="flex flex-wrap items-center justify-between">
            <div class="flex items-center">
              <h2 class="text-xl font-bold text-gray-800">
                <i class="fas fa-bullhorn mr-2 text-blue-500"></i>
                Gestion des Notifications Sellers / Bannières
              </h2>
            </div>
            <button
              @click="openCreateModal"
              class="bg-blue-500 text-white active:bg-blue-600 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150"
            >
              <i class="fas fa-plus mr-1"></i> Nouvelle Notification
            </button>
          </div>
        </div>

        <!-- Filters -->
        <div class="px-6 py-4 bg-gray-50 border-b">
          <div class="flex flex-wrap items-center gap-4">
            <div class="flex-1 min-w-[200px]">
              <label class="block text-gray-700 text-sm font-bold mb-2">Warehouse</label>
              <v-select
                v-model="filters.warehouse"
                :options="warehouses"
                label="name"
                :reduce="w => w._id"
                placeholder="Tous les warehouses"
                class="style-chooser border border-gray-500 rounded"
              >
                <template #option="option">
                  <div class="flex items-center">
                    <img v-if="option.flag" :src="option.flag" class="w-5 h-4 mr-2 rounded" />
                    {{ option.name }}
                  </div>
                </template>
              </v-select>
            </div>
            <div class="min-w-[150px]">
              <label class="block  text-gray-700 text-sm font-bold mb-2">Statut</label>
              <select v-model="filters.isActive" class="w-full border border-gray-500 rounded px-3 py-2 text-gray-700">
                <option :value="null">Tous</option>
                <option :value="true">Actif</option>
                <option :value="false">Inactif</option>
              </select>
            </div>
            <div class="flex items-end mt-auto">
              <button
                @click="fetchNotifications"
                :disabled="loading"
                class="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:opacity-50"
              >
                <i class="fas fa-search mr-1"></i> Rechercher
              </button>
            </div>
          </div>
        </div>

        <!-- Table -->
        <div class="block w-full overflow-x-auto p-6">
          <div v-if="loading" class="text-center py-8">
            <i class="fas fa-spinner fa-spin text-3xl text-blue-500"></i>
            <p class="mt-2 text-gray-500">Chargement...</p>
          </div>

          <table v-else class="items-center w-full bg-transparent border-collapse">
            <thead>
              <tr>
                <th class="table-header">Titre</th>
                <th class="table-header">Message</th>
                <th class="table-header">Type</th>
                <th class="table-header">Warehouses</th>
                <th class="table-header">Priorité</th>
                <th class="table-header">Période</th>
                <th class="table-header">Statut</th>
                <th class="table-header">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="notifications.length === 0">
                <td colspan="8" class="text-center py-8 text-gray-500">
                  <i class="fas fa-inbox text-4xl mb-2"></i>
                  <p>Aucune notification trouvée</p>
                </td>
              </tr>
              <tr v-for="notif in notifications" :key="notif._id" class="hover:bg-gray-50">
                <td class="table-cell font-medium">{{ notif.title }}</td>
                <td class="table-cell">
                  <span class="truncate-text" v-html="notif.message"></span>
                </td>
                <td class="table-cell">
                  <span :class="getTypeBadgeClass(notif.type)">
                    <i :class="getTypeIcon(notif.type)" class="mr-1"></i>
                    {{ notif.type }}
                  </span>
                </td>
                <td class="table-cell">
                  <div v-if="notif.warehouses && notif.warehouses.length > 0" class="flex flex-wrap gap-1">
                    <span 
                      v-for="wh in getWarehouseNames(notif.warehouses)" 
                      :key="wh._id"
                      class="px-2 py-1 text-xs bg-gray-100 rounded"
                    >
                      {{ wh.name }}
                    </span>
                  </div>
                  <span v-else class="text-gray-400 text-sm">Tous</span>
                </td>
                <td class="table-cell">
                  <span :class="getPriorityBadgeClass(notif.priority)">
                    {{ getPriorityLabel(notif.priority) }}
                  </span>
                </td>
                <td class="table-cell text-sm">
                  <div v-if="notif.startDate || notif.endDate">
                    <div v-if="notif.startDate">De: {{ formatDate(notif.startDate) }}</div>
                    <div v-if="notif.endDate">À: {{ formatDate(notif.endDate) }}</div>
                  </div>
                  <span v-else class="text-gray-400">Permanent</span>
                </td>
                <td class="table-cell">
                  <button 
                    @click="toggleNotification(notif)"
                    :class="notif.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'"
                    class="px-3 py-1 rounded-full text-sm font-medium transition-colors"
                  >
                    {{ notif.isActive ? 'Actif' : 'Inactif' }}
                  </button>
                </td>
                <td class="table-cell">
                  <div class="flex items-center gap-2">
                    <button
                      @click="openEditModal(notif)"
                      class="text-blue-500 hover:text-blue-700"
                      title="Modifier"
                    >
                      <i class="fas fa-edit"></i>
                    </button>
                    <button
                      @click="confirmDelete(notif)"
                      class="text-red-500 hover:text-red-700"
                      title="Supprimer"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal Create/Edit -->
    <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen px-4">
        <div class="fixed inset-0 bg-black opacity-50" @click="closeModal"></div>
        
        <div class="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div class="px-6 py-4 border-b flex justify-between items-center">
            <h3 class="text-lg font-bold text-gray-800">
              {{ isEditing ? 'Modifier la Notification' : 'Nouvelle Notification' }}
            </h3>
            <button @click="closeModal" class="text-gray-400 hover:text-gray-600">
              <i class="fas fa-times text-xl"></i>
            </button>
          </div>

          <div class="px-6 py-4">
            <div class="space-y-4">
              <!-- Title -->
              <div>
                <label class="block text-gray-700 text-sm font-bold mb-2">
                  Titre <span class="text-red-500">*</span>
                </label>
                <input
                  v-model="form.title"
                  type="text"
                  maxlength="100"
                  class="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Titre de la notification"
                />
              </div>

              <!-- Message -->
              <div>
                <label class="block text-gray-700 text-sm font-bold mb-2">
                  Message <span class="text-red-500">*</span>
                </label>

                <vue-editor
                  maxlength="500"
                  placeholder="Contenu du message..."
                  v-model="form.message"
                  ref="editor"
                  class="notification-editor"
                >

                </vue-editor>
                
                <p class="text-sm text-gray-400 mt-1">{{ form.message.length }}/500</p>
              </div>

              <!-- Type -->
              <div>
                <label class="block text-gray-700 text-sm font-bold mb-2">Type</label>
                <div class="flex flex-wrap gap-3">
                  <label 
                    v-for="t in notificationTypes" 
                    :key="t.value"
                    class="flex items-center cursor-pointer"
                  >
                    <input
                      type="radio"
                      v-model="form.type"
                      :value="t.value"
                      class="mr-2"
                    />
                    <span :class="getTypeBadgeClass(t.value)" class="px-2 py-1 rounded text-sm">
                      <i :class="getTypeIcon(t.value)" class="mr-1"></i>
                      {{ t.label }}
                    </span>
                  </label>
                </div>
              </div>

              <!-- Warehouses -->
              <div>
                <label class="block text-gray-700 text-sm font-bold mb-2">
                  Warehouses (laisser vide pour tous)
                </label>
                <v-select
                  v-model="form.warehouses"
                  :options="warehouses"
                  label="name"
                  :reduce="w => w._id"
                  multiple
                  placeholder="Sélectionner les warehouses..."
                  class="style-chooser border border-gray-500 rounded"
                >
                  <template #option="option">
                    <div class="flex items-center">
                      <img v-if="option.flag" :src="option.flag" class="w-5 h-4 mr-2 rounded" />
                      {{ option.name }}
                    </div>
                  </template>
                </v-select>
              </div>

              <!-- Priority -->
              <div>
                <label class="block text-gray-700 text-sm font-bold mb-2">Priorité</label>
                <select v-model="form.priority" class="w-full border rounded px-3 py-2">
                  <option :value="0">Normale</option>
                  <option :value="1">Haute</option>
                  <option :value="2">Urgente</option>
                </select>
              </div>

              <!-- Date Range -->
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="block text-gray-700 text-sm font-bold mb-2">Date début</label>
                  <input
                    v-model="form.startDate"
                    type="datetime-local"
                    class="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label class="block text-gray-700 text-sm font-bold mb-2">Date fin</label>
                  <input
                    v-model="form.endDate"
                    type="datetime-local"
                    class="w-full border rounded px-3 py-2"
                  />
                </div>
              </div>

              <!-- Active -->
              <div class="flex items-center">
                <input
                  type="checkbox"
                  v-model="form.isActive"
                  id="isActive"
                  class="w-4 h-4 text-blue-600 rounded"
                />
                <label for="isActive" class="ml-2 text-gray-700 font-medium">
                  Notification active
                </label>
              </div>
            </div>
          </div>

          <div class="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
            <button
              @click="closeModal"
              class="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Annuler
            </button>
            <button
              @click="saveNotification"
              :disabled="saving || !form.title || !form.message"
              class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
            >
              <i v-if="saving" class="fas fa-spinner fa-spin mr-1"></i>
              {{ isEditing ? 'Mettre à jour' : 'Créer' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen px-4">
        <div class="fixed inset-0 bg-black opacity-50" @click="showDeleteModal = false"></div>
        
        <div class="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div class="text-center">
            <i class="fas fa-exclamation-triangle text-5xl text-yellow-500 mb-4"></i>
            <h3 class="text-lg font-bold text-gray-800 mb-2">Confirmer la suppression</h3>
            <p class="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer la notification "{{ notificationToDelete.title }}" ?
            </p>
            <div class="flex justify-center gap-3">
              <button
                @click="showDeleteModal = false"
                class="px-4 py-2 border rounded text-gray-600 hover:bg-gray-100"
              >
                Annuler
              </button>
              <button
                @click="deleteNotification"
                :disabled="deleting"
                class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
              >
                <i v-if="deleting" class="fas fa-spinner fa-spin mr-1"></i>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { VueEditor } from "vue2-editor";
export default {
  name: "NotificationsManagement",
  props: {
    currentUser: { type: Object },
    idWharhouse: { type: Object }
  },
  components: {
    VueEditor,
  },
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      notifications: [],
      warehouses: [],
      filters: {
        warehouse: null,
        isActive: null
      },
      showModal: false,
      showDeleteModal: false,
      isEditing: false,
      editingId: null,
      notificationToDelete: null,
      form: {
        title: '',
        message: '',
        type: 'info',
        warehouses: [],
        isActive: true,
        startDate: '',
        endDate: '',
        priority: 0
      },
      notificationTypes: [
        { value: 'info', label: 'Info' },
        { value: 'warning', label: 'Avertissement' },
        { value: 'success', label: 'Succès' },
        { value: 'danger', label: 'Urgent' }
      ]
    };
  },
  async mounted() {
    await this.fetchWarehouses();
    await this.fetchNotifications();
  },
  methods: {
    async fetchWarehouses() {
      try {
        const res = await this.$server.search("warehouses", { limit: 100 });
        if (res.content.results) {
          this.warehouses = res.content.results;
        }
      } catch (error) {
        console.error("Error fetching warehouses:", error);
      }
    },

    async fetchNotifications() {
      this.loading = true;
      try {
        const filters = {};
        if (this.filters.warehouse) filters.warehouse = this.filters.warehouse;
        if (this.filters.isActive !== null) filters.isActive = this.filters.isActive;

        const res = await this.$server.getNotifications("settings", filters);
        if (res.content && Array.isArray(res.content)) {
          this.notifications = res.content;
        }else  this.$toast.warning(res.content);
      } catch (error) {
        console.error("Error fetching notifications:", error);
        this.$toast.error("Erreur lors du chargement des notifications");
      }
      this.loading = false;
    },

    openCreateModal() {
      this.isEditing = false;
      this.editingId = null;
      this.form = {
        title: '',
        message: '',
        type: 'info',
        warehouses: [],
        isActive: true,
        startDate: '',
        endDate: '',
        priority: 0
      };
      this.showModal = true;
    },

    openEditModal(notif) {
      this.isEditing = true;
      this.editingId = notif._id;
      this.form = {
        title: notif.title,
        message: notif.message,
        type: notif.type,
        warehouses: notif.warehouses.map(w => w._id || w),
        isActive: notif.isActive,
        startDate: notif.startDate ? this.formatDateForInput(notif.startDate) : '',
        endDate: notif.endDate ? this.formatDateForInput(notif.endDate) : '',
        priority: notif.priority
      };
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
    },

    async saveNotification() {
      this.saving = true;
      try {
        const data = {
          ...this.form,
          startDate: this.form.startDate || null,
          endDate: this.form.endDate || null
        };

        let res;
        if (this.isEditing) {
          data.notificationId = this.editingId;
          res = await this.$server.updateNotification("settings", data);
        } else {
          res = await this.$server.createNotification("settings", data);
        }

        if (res.status === 200) {
          this.$toast.success(this.isEditing ? "Notification mise à jour" : "Notification créée");
          this.closeModal();
          await this.fetchNotifications();
        } else {
          this.$toast.error(res.content || "Erreur lors de l'enregistrement");
        }
      } catch (error) {
        console.error("Error saving notification:", error);
        this.$toast.error("Erreur lors de l'enregistrement");
      }
      this.saving = false;
    },

    async toggleNotification(notif) {
      try {
        const res = await this.$server.toggleNotification("settings", {
          notificationId: notif._id
        });
        if (res.status === 200) {
          notif.isActive = !notif.isActive;
          this.$toast.success(`Notification ${notif.isActive ? 'activée' : 'désactivée'}`);
        }
      } catch (error) {
        console.error("Error toggling notification:", error);
        this.$toast.error("Erreur lors de la modification");
      }
    },

    confirmDelete(notif) {
      this.notificationToDelete = notif;
      this.showDeleteModal = true;
    },

    async deleteNotification() {
      this.deleting = true;
      try {
        const res = await this.$server.deleteNotification("settings", {
          notificationId: this.notificationToDelete._id
        });
        if (res.status === 200) {
          this.$toast.success("Notification supprimée");
          this.showDeleteModal = false;
          await this.fetchNotifications();
        }
      } catch (error) {
        console.error("Error deleting notification:", error);
        this.$toast.error("Erreur lors de la suppression");
      }
      this.deleting = false;
    },

    getTypeBadgeClass(type) {
      const classes = {
        info: 'bg-blue-100 text-blue-700',
        warning: 'bg-yellow-100 text-yellow-700',
        success: 'bg-green-100 text-green-700',
        danger: 'bg-red-100 text-red-700'
      };
      return classes[type] || classes.info;
    },

    getTypeIcon(type) {
      const icons = {
        info: 'fas fa-info-circle',
        warning: 'fas fa-exclamation-triangle',
        success: 'fas fa-check-circle',
        danger: 'fas fa-exclamation-circle'
      };
      return icons[type] || icons.info;
    },

    getPriorityBadgeClass(priority) {
      const classes = {
        0: 'bg-gray-100 text-gray-600',
        1: 'bg-orange-100 text-orange-700',
        2: 'bg-red-100 text-red-700'
      };
      return `px-2 py-1 rounded text-xs font-medium ${classes[priority] || classes[0]}`;
    },

    getPriorityLabel(priority) {
      const labels = { 0: 'Normale', 1: 'Haute', 2: 'Urgente' };
      return labels[priority] || 'Normale';
    },

    getWarehouseNames(warehouseIds) {
      return warehouseIds.map(id => {
        const wh = this.warehouses.find(w => w._id === (id._id || id));
        return wh || { name: 'Unknown' };
      });
    },

    formatDate(date) {
      if (!date) return '';
      return this.$moment(date).format('DD/MM/YYYY HH:mm');
    },

    formatDateForInput(date) {
      if (!date) return '';
      return this.$moment(date).format('YYYY-MM-DDTHH:mm');
    }
  }
};
</script>

<style scoped>
  .table-header {
    padding: 0.75rem 1rem;
    text-align: left;
    font-size: 0.75rem;
    font-weight: 600;
    color: #4b5563;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background-color: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
  }
  .table-cell {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e5e7eb;
    font-size: 0.875rem;
    color: #374151;
  }
  .truncate-text {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    max-width: 200px;
  }
.notification-editor >>> .ql-toolbar {
  border-bottom: 1px solid #e2e8f0;
  padding: 8px;
  background-color: #f9fafb;
}

.notification-editor >>> .ql-container {
  min-height: 120px;
}
</style>